/**
 * useLocalStorageList — lista persistente em localStorage, max N items.
 *
 * Modo:
 *  - 'fifo': novo entra no topo, mais antigo sai quando excede max (uso: recents)
 *  - 'set':  toggle add/remove, sem rotacao automatica (uso: pinned com max)
 *
 * SSR-safe (lazy init), sync entre abas via storage event.
 */

import { useCallback, useEffect, useState } from 'react';

type Mode = 'fifo' | 'set';

interface Options {
  key: string;
  max: number;
  mode: Mode;
}

function readStorage(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeStorage(key: string, items: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Quota ou storage indisponivel — silent fail.
  }
}

export function useLocalStorageList({ key, max, mode }: Options) {
  const [items, setItems] = useState<string[]>(() => readStorage(key).slice(0, max));

  // Sync entre abas
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== key) return;
      setItems(readStorage(key).slice(0, max));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, max]);

  const add = useCallback(
    (item: string) => {
      setItems((prev) => {
        // Remove duplicata, adiciona no topo, trunca
        const dedup = prev.filter((x) => x !== item);
        const next = [item, ...dedup].slice(0, max);
        writeStorage(key, next);
        return next;
      });
    },
    [key, max],
  );

  const remove = useCallback(
    (item: string) => {
      setItems((prev) => {
        const next = prev.filter((x) => x !== item);
        writeStorage(key, next);
        return next;
      });
    },
    [key],
  );

  const toggle = useCallback(
    (item: string): { ok: boolean; reason?: 'at-max' } => {
      const current = readStorage(key).slice(0, max);
      if (current.includes(item)) {
        const next = current.filter((x) => x !== item);
        writeStorage(key, next);
        setItems(next);
        return { ok: true };
      }
      if (mode === 'set' && current.length >= max) {
        return { ok: false, reason: 'at-max' };
      }
      const next = [item, ...current].slice(0, max);
      writeStorage(key, next);
      setItems(next);
      return { ok: true };
    },
    [key, max, mode],
  );

  const has = useCallback((item: string) => items.includes(item), [items]);

  const clear = useCallback(() => {
    writeStorage(key, []);
    setItems([]);
  }, [key]);

  return { items, add, remove, toggle, has, clear };
}
